import { ChartStuff } from "@/lib/deriveChartStuff";
import { formatPrice } from "@/lib/formatters";
import { NormalizedDataPoint } from "@/lib/PreparedDataset";
import * as d3 from "d3";
import { useEffect, useState } from "react";
import { CompSummaryTooltip, deriveNonOverlappingTooltipPositions } from "./ChartTooltip";

const TOOLTIP_WIDTH = 200;

type SummaryTooltipsProps = {
  subject?: { date: Date; name: string };
  chartStuff: ChartStuff | null;
  data: NormalizedDataPoint[];
  xScale: d3.ScaleLinear<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  containerRef: React.RefObject<HTMLDivElement>;
};

export function SummaryTooltips({
  subject,
  chartStuff,
  data,
  xScale,
  yScale,
  containerRef,
}: SummaryTooltipsProps) {
  const [nonOverlappingTooltipPositions, setNonOverlappingTooltipPositions] = useState<
    any[] | null
  >(null);

  type TooltipHeights = {
    [key: string]: number;
  };
  const [tooltipHeights, setTooltipHeights] = useState<TooltipHeights>({});

  useEffect(() => {
    if (chartStuff?.adjustedCompData) {
      const input = chartStuff.adjustedCompData
        .map((i) => {
          if (!i.adjustedData) return null;
          const xPosition = xScale(i.subjectSaleDate);
          const yPosition = yScale(i.adjustedData.adjustedValue);
          // isFinite, not falsy: the highest adjusted value maps to y=0, a valid position.
          if (!Number.isFinite(xPosition) || !Number.isFinite(yPosition)) return null;
          return {
            compId: i.compId,
            position: {
              x: xPosition,
              y: yPosition,
            },
            width: TOOLTIP_WIDTH,
            height: tooltipHeights[i.compId],
          };
        })
        .filter((i) => i !== null);

      const value = deriveNonOverlappingTooltipPositions(input);

      setNonOverlappingTooltipPositions((prevValue) => {
        if (JSON.stringify(prevValue) === JSON.stringify(value)) return prevValue;
        return value;
      });
    }
  }, [
    JSON.stringify(chartStuff?.adjustedCompData),
    xScale,
    yScale,
    JSON.stringify(tooltipHeights),
  ]);

  if (!subject?.date || !chartStuff?.regressionData || !chartStuff?.adjustedCompData) {
    return null;
  }

  return (
    <>
      {chartStuff.adjustedCompData?.map((adjustedComp) => {
        const comp = adjustedComp;
        const adjustedCompData = adjustedComp.adjustedData;
        if (!adjustedCompData) return null;
        const compWithTooltipPosition = nonOverlappingTooltipPositions?.find(
          (i) => i.compId === comp.compId,
        );

        if (!compWithTooltipPosition) return null;

        const d = data.find((d) => d.id === comp.compId);
        if (!d) return null;

        const portalContainer = containerRef.current;
        if (!portalContainer) return null;

        return (
          <CompSummaryTooltip
            portalContainer={portalContainer}
            onHeightChange={async (height) => {
              setTooltipHeights((prevHeights) => ({
                ...prevHeights,
                [comp.compId]: height,
              }));
            }}
            key={comp.compId}
            boxPosition={compWithTooltipPosition.boxPosition}
            position={compWithTooltipPosition.position}
            svgTop={0}
            svgLeft={0}
          >
            <div className="w-full text-sm">
              <div>{adjustedCompData.address}</div>
              <div
                className={
                  adjustedCompData.adjustmentAmount >= 0
                    ? "font-bold text-green-600"
                    : "font-bold text-red-600"
                }
              >
                {adjustedCompData.adjustmentAmount >= 0 ? (
                  <span className="text-green-600">
                    +{formatPrice(adjustedCompData.adjustmentAmount)}
                  </span>
                ) : (
                  <span className="text-red-600">
                    -{formatPrice(-1 * adjustedCompData.adjustmentAmount)}
                  </span>
                )}
              </div>
            </div>
          </CompSummaryTooltip>
        );
      })}
    </>
  );
}
